import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./TableOrdering.css";
import { PosCustomButton } from "../../../customComponents/PosCustomButton/PosCustomButton";
import PosCustomIcon from "../../../customComponents/PosCustomIcon/PosCustomIcon";
import {
  ArrowLeftIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import {
  completeTaskByWaiter,
  deleteAllTasksForTable,
  getAllTablePins,
  getTableTasksForWaiters,
} from "../../../config/Services";
import { useSnackbar } from "../product/hooks/SnackbarProvider";

const TableOrdering = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("tablePins");

  // Table Pins State
  const [tablePins, setTablePins] = useState([]);
  const [pinStatusFilter, setPinStatusFilter] = useState("AUTHORIZATION_PENDING");
  const [pinSearchInput, setPinSearchInput] = useState("");
  const [pinSearchQuery, setPinSearchQuery] = useState("");
  const pinSearchTimer = useRef(null);
  const [pinsCurrentPage, setPinsCurrentPage] = useState(0);
  const [pinsTotalPages, setPinsTotalPages] = useState(0);
  const [pinsTotalElements, setPinsTotalElements] = useState(0);
  const [pinsLoading, setPinsLoading] = useState(false);

  // Waiter Tasks State
  const [waiterTasks, setWaiterTasks] = useState([]);
  const [taskFilter, setTaskFilter] = useState("REQUESTED"); // REQUESTED or DONE
  const [tasksCurrentPage, setTasksCurrentPage] = useState(0);
  const [tasksTotalPages, setTasksTotalPages] = useState(0);
  const [tasksTotalElements, setTasksTotalElements] = useState(0);
  const [tasksLoading, setTasksLoading] = useState(false);

  const [pageSize] = useState(10);
  const showSnackbar = useSnackbar();

  const orgId = localStorage.getItem("orgId");
  const locId = localStorage.getItem("locId");
  const layoutId = localStorage.getItem("layoutId");
  const retailUserLocalStorageData = JSON.parse(localStorage.getItem("retailUserDetails"));

  // Debounce search input
  const handlePinSearchChange = (e) => {
    const value = e.target.value;
    setPinSearchInput(value);
    if (pinSearchTimer.current) clearTimeout(pinSearchTimer.current);
    pinSearchTimer.current = setTimeout(() => {
      setPinSearchQuery(value.trim());
    }, 500);
  };

  // Fetch Table Pins
  useEffect(() => {
    if (activeTab === "tablePins") {
      fetchTablePins();
    }
  }, [pinsCurrentPage, activeTab, pinStatusFilter, pinSearchQuery]);

  // Reset pins page when filter or search changes
  useEffect(() => {
    setPinsCurrentPage(0);
  }, [pinStatusFilter, pinSearchQuery]);

  // Fetch Waiter Tasks
  useEffect(() => {
    if (activeTab === "waiterTasks") {
      fetchWaiterTasks();
    }
  }, [activeTab, taskFilter, tasksCurrentPage]);

  // Reset task page when filter changes
  useEffect(() => {
    setTasksCurrentPage(0);
  }, [taskFilter]);

  const fetchTablePins = async () => {
    const payload = {
      organisationId: orgId,
      locationId: locId,
      status: [pinStatusFilter],
      page: pinsCurrentPage,
      size: pageSize,
      ...(pinSearchQuery && { tableNumber: pinSearchQuery }),
    };
    try {
      setPinsLoading(true);
      const response = await getAllTablePins(payload);
      if (response && response?.data?.content) {
        setTablePins(response?.data?.content);
        setPinsTotalPages(response?.data?.totalPages);
        setPinsTotalElements(response?.data?.totalElements);
      }
    } catch (error) {
      console.error("Error fetching table pins:", error);
    } finally {
      setPinsLoading(false);
    }
  };

  const fetchWaiterTasks = async () => {
    try {
      setTasksLoading(true);
      const payload = {
        organisationId: orgId,
        locationId: locId,
        layoutId: layoutId,
        status: taskFilter,
        page: tasksCurrentPage,
        size: pageSize,
      };

      const response = await getTableTasksForWaiters(payload);

      if (response?.data && response?.data?.data) {
        setTasksTotalPages(response.data.data.totalPages || 0);
        setTasksTotalElements(response.data.data.totalElements || 0);
        setWaiterTasks(response.data.data.content || []);
      }
    } catch (error) {
      console.error("Error fetching waiter tasks:", error);
    } finally {
      setTasksLoading(false);
    }
  };

  const handleCompleteTask = async (taskId) => {
    const payload = {
      taskId: taskId,
      completedBy: retailUserLocalStorageData?.firstName,
    };
    try {
      const response = await completeTaskByWaiter(payload);

      if (response?.data?.status === false) {
        showSnackbar(response?.data?.message || "Error while marking this task complete", "error");
        return;
      }

      showSnackbar(response?.data?.message);
      fetchWaiterTasks();
    } catch (error) {
      showSnackbar("Error while marking this task complete", "error");
    }
  };

  const handleCompleteAllTableTasks = async (table) => {
    try {
      const tableId = table.tableMetadataId;

      if (!tableId) {
        return;
      }

      const response = await deleteAllTasksForTable(tableId);
      if (response?.data?.status === false) {
        showSnackbar(response?.data?.message || "Error while marking this task complete", "error");
        return;
      }

      showSnackbar(response?.data?.message);

      fetchWaiterTasks();
    } catch (error) {
      showSnackbar("Error while marking this task complete", "error");
    }
  };

  const formatTaskDescription = (taskAction) => {
    const descriptions = {
      REQUEST_FOR_ASSISTANCE: "Customer needs assistance with menu",
      REQUEST_FOR_WATER: "Water refill requested",
      REQUEST_FOR_BILL: "Bill requested",
      REQUEST_FOR_ORDER_UPDATES: "Customer asking about order status",
      REQUEST_FOR_PAY_BILL: "Customer ready to pay",
    };
    return descriptions[taskAction] || "Task pending";
  };

  const getTaskTypeColor = (taskType) => {
    const colors = {
      REQUEST_FOR_ASSISTANCE: "#FF9800",
      REQUEST_FOR_WATER: "#2196F3",
      REQUEST_FOR_BILL: "#4CAF50",
      REQUEST_FOR_ORDER_UPDATES: "#9C27B0",
      REQUEST_FOR_PAY_BILL: "#F44336",
      DEFAULT: "#757575",
    };
    return colors[taskType] || colors.DEFAULT;
  };

  const getTaskTypeIcon = (taskType) => {
    const icons = {
      REQUEST_FOR_ASSISTANCE: "🙋",
      REQUEST_FOR_WATER: "💧",
      REQUEST_FOR_BILL: "🧾",
      REQUEST_FOR_ORDER_UPDATES: "📋",
      REQUEST_FOR_PAY_BILL: "💳",
      DEFAULT: "📌",
    };
    return icons[taskType] || icons.DEFAULT;
  };

  const formatTaskType = (taskType) => {
    return taskType
      .replace(/REQUEST_FOR_/g, "")
      .replace(/_/g, " ")
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMins = Math.floor((now - date) / 60000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      AUTHORIZED_CLOSED: "status-closed",
      AUTHORIZED: "status-active",
      AUTHORIZATION_PENDING: "status-pending",
    };
    return statusClasses[status] || "status-default";
  };

  const getStatusName = (status) => {
    const statusClass = {
      AUTHORIZED_CLOSED: "Closed",
      AUTHORIZED: "Active",
      AUTHORIZATION_PENDING: "Pending",
    };
    return statusClass[status] || "status-default";
  };

  return (
    <div className="table-ordering-container">
      <div className="table-ordering-header">
        <div className="header-left">
          <PosCustomButton
            variant="white-d"
            iconOnLeft={<PosCustomIcon icon={<ArrowLeftIcon />} height={"20px"} width={"20px"} />}
            onClickFunction={() => navigate(-1)}
          />
          <h2>Table Ordering</h2>
        </div>
      </div>

      <div className="table-ordering-tabs">
        <button
          className={`tab-button ${activeTab === "tablePins" ? "active" : ""}`}
          onClick={() => setActiveTab("tablePins")}
        >
          Table Pins ({pinsTotalElements})
        </button>
        <button
          className={`tab-button ${activeTab === "waiterTasks" ? "active" : ""}`}
          onClick={() => setActiveTab("waiterTasks")}
        >
          Waiter Tasks ({taskFilter === "REQUESTED" ? tasksTotalElements : "History"})
        </button>
      </div>

      <div className="table-ordering-content">
        {activeTab === "tablePins" && (
          <div className="table-pins-section">
            <div className="pins-filter-row">
              <div className="task-filter-buttons">
                <button
                  className={`filter-button ${pinStatusFilter === "AUTHORIZATION_PENDING" ? "active" : ""}`}
                  onClick={() => setPinStatusFilter("AUTHORIZATION_PENDING")}
                >
                  Pending
                </button>
                <button
                  className={`filter-button ${pinStatusFilter === "AUTHORIZED" ? "active" : ""}`}
                  onClick={() => setPinStatusFilter("AUTHORIZED")}
                >
                  Authorized
                </button>
              </div>
              <input
                type="text"
                className="pin-search-input"
                placeholder="Search by table number..."
                value={pinSearchInput}
                onChange={handlePinSearchChange}
              />
            </div>
            {pinsLoading ? (
              <div className="loading-state">
                <p>Loading table pins...</p>
              </div>
            ) : tablePins.length === 0 ? (
              <div className="empty-state">
                <p>No tables available</p>
              </div>
            ) : (
              <>
                <div className="pins-table-container">
                  <table className="pins-table">
                    <thead>
                      <tr>
                        <th>Table Number</th>
                        <th>PIN</th>
                        <th>Status</th>
                        <th>Created Date</th>
                        <th>Session Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tablePins.map((item) => (
                        <tr key={item.sessionId}>
                          <td>
                            <span className="table-number-cell">Table {item.tableNumber}</span>
                          </td>
                          <td>
                            <span className="pin-code-cell">{item.pin}</span>
                          </td>
                          <td>
                            <span className={`status-badge ${getStatusBadgeClass(item.status)}`}>
                              {getStatusName(item.status)}
                            </span>
                          </td>
                          <td>
                            <span className="date-cell">{formatDate(item.createdDate)}</span>
                          </td>
                          <td>
                            <span
                              className={`active-indicator ${
                                item.sessionActive ? "active" : "inactive"
                              }`}
                            >
                              {item.sessionActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {pinsTotalPages > 1 && (
                  <div className="pagination-controls">
                    <div className="pagination-info">
                      Showing {pinsCurrentPage * pageSize + 1} to{" "}
                      {Math.min((pinsCurrentPage + 1) * pageSize, pinsTotalElements)} of{" "}
                      {pinsTotalElements}
                    </div>
                    <div className="pagination-buttons">
                      <PosCustomButton
                        variant="white-d"
                        iconOnLeft={
                          <PosCustomIcon
                            icon={<ChevronLeftIcon />}
                            height={"18px"}
                            width={"18px"}
                          />
                        }
                        onClickFunction={() => setPinsCurrentPage((p) => Math.max(0, p - 1))}
                        disabled={pinsCurrentPage === 0}
                      />
                      <span className="page-indicator">
                        Page {pinsCurrentPage + 1} of {pinsTotalPages}
                      </span>
                      <PosCustomButton
                        variant="white-d"
                        iconOnLeft={
                          <PosCustomIcon
                            icon={<ChevronRightIcon />}
                            height={"18px"}
                            width={"18px"}
                          />
                        }
                        onClickFunction={() => setPinsCurrentPage((p) => p + 1)}
                        disabled={pinsCurrentPage >= pinsTotalPages - 1}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === "waiterTasks" && (
          <div className="waiter-tasks-section">
            <div className="task-filter-buttons">
              <button
                className={`filter-button ${taskFilter === "REQUESTED" ? "active" : ""}`}
                onClick={() => setTaskFilter("REQUESTED")}
              >
                Active Tasks
              </button>
              <button
                className={`filter-button ${taskFilter === "DONE" ? "active" : ""}`}
                onClick={() => setTaskFilter("DONE")}
              >
                Closed Tasks
              </button>
            </div>

            {tasksLoading ? (
              <div className="loading-state">
                <p>Loading tasks...</p>
              </div>
            ) : waiterTasks.length === 0 ? (
              <div className="empty-state">
                <p>No {taskFilter.toLowerCase()} tasks found</p>
              </div>
            ) : (
              <>
                <div className="tasks-grid">
                  {waiterTasks.map((table) => (
                    <div key={table.tableMetadataId} className="table-group-card">
                      <div className="table-group-header">
                        <div className="table-info">
                          <h3>{table.tableName}</h3>
                          <span>
                            {table.floorName} • {table.areaName}
                          </span>
                        </div>
                        <div className="header-actions">
                          {taskFilter === "REQUESTED" && (
                            <button
                              className="complete-all-btn"
                              onClick={() => handleCompleteAllTableTasks(table)}
                            >
                              Complete All
                            </button>
                          )}
                          <span className="task-count-badge">
                            {table.tasks?.length || 0}{" "}
                            {table.tasks?.length === 1 ? "Task" : "Tasks"}
                          </span>
                        </div>
                      </div>

                      <div className="table-tasks-list">
                        {table.tasks?.map((task) => (
                          <div key={task.taskId} className="individual-task-item">
                            <div
                              className="mini-task-icon"
                              style={{ color: getTaskTypeColor(task.taskAction) }}
                            >
                              {getTaskTypeIcon(task.taskAction)}
                            </div>
                            <div className="task-item-content">
                              <div className="task-item-main">
                                <strong>{formatTaskType(task.taskAction)}</strong>
                                <span className="task-item-time">
                                  {getTimeAgo(`${task.requestedDate}T${task.requestedAt}`)}
                                </span>
                              </div>
                              <p className="task-item-desc">
                                {formatTaskDescription(task.taskAction)}
                              </p>
                              {task.completedAt && (
                                <small className="done-by">
                                  Done by: {task.completedBy} at {task.completedAt}
                                </small>
                              )}
                            </div>
                            {taskFilter === "REQUESTED" && (
                              <button
                                className="approve-mini-btn"
                                onClick={() => handleCompleteTask(task.taskId)}
                                title="Approve Task"
                              >
                                <CheckIcon className="check-icon-small" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {tasksTotalPages > 1 && (
                  <div className="pagination-controls">
                    <div className="pagination-info">
                      Showing {tasksCurrentPage * pageSize + 1} to{" "}
                      {Math.min((tasksCurrentPage + 1) * pageSize, tasksTotalElements)} of{" "}
                      {tasksTotalElements}
                    </div>
                    <div className="pagination-buttons">
                      <PosCustomButton
                        variant="white-d"
                        iconOnLeft={
                          <PosCustomIcon
                            icon={<ChevronLeftIcon />}
                            height={"18px"}
                            width={"18px"}
                          />
                        }
                        onClickFunction={() => setTasksCurrentPage((p) => Math.max(0, p - 1))}
                        disabled={tasksCurrentPage === 0}
                      />
                      <span className="page-indicator">
                        Page {tasksCurrentPage + 1} of {tasksTotalPages}
                      </span>
                      <PosCustomButton
                        variant="white-d"
                        iconOnLeft={
                          <PosCustomIcon
                            icon={<ChevronRightIcon />}
                            height={"18px"}
                            width={"18px"}
                          />
                        }
                        onClickFunction={() => setTasksCurrentPage((p) => p + 1)}
                        disabled={tasksCurrentPage >= tasksTotalPages - 1}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TableOrdering;
